/**
 * Projects API service for backend project persistence.
 * Handles CRUD operations for projects stored in DynamoDB.
 */

import type { ProjectConfig, ProjectType } from '@/lib/types';
import type { SaveTemplateRequest, TemplateMetadata } from './templatesApi';
import { BaseApiService } from './BaseApiService';
import { API_URL } from '@/lib/apiConfig';

// API response types
export interface ProjectApiResponse {
  id: string;
  name: string;
  type: ProjectType | null;
  description: string;
  templateIds: string[];
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ListProjectsResponse {
  projects: ProjectApiResponse[];
}

export interface CreateProjectRequest {
  name: string;
  type?: ProjectType | null;
  description?: string;
  templateIds?: string[];
}

export interface UpdateProjectRequest {
  name?: string;
  type?: ProjectType | null;
  description?: string;
  templateIds?: string[];
  isArchived?: boolean;
}

/**
 * Convert API response to ProjectConfig format used by frontend
 */
function toProjectConfig(apiProject: ProjectApiResponse): ProjectConfig {
  return {
    id: apiProject.id,
    projectName: apiProject.name,
    type: apiProject.type,
    description: apiProject.description || undefined,
    templateIds: apiProject.templateIds || [],
    createdAt: apiProject.createdAt,
    updatedAt: apiProject.updatedAt,
    isArchived: apiProject.isArchived,
  };
}

class ProjectsApiService extends BaseApiService {
  constructor() {
    super(API_URL, 'ProjectsApi');
  }

  /**
   * List all projects for the authenticated user.
   */
  async listProjects(includeArchived = false, templateId?: string): Promise<ProjectConfig[]> {
    this.log.debug('Listing projects', { action: 'listProjects', includeArchived, templateId });
    const headers = await this.getHeaders();

    const searchParams = new URLSearchParams();
    if (includeArchived) searchParams.set('includeArchived', 'true');
    if (templateId) searchParams.set('templateId', templateId);
    const queryString = searchParams.toString();
    const url = `${this.baseUrl}/projects${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to list projects', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to list projects');
    }

    const data: ListProjectsResponse = await response.json();
    this.log.info('Projects listed', { action: 'listProjects', count: data.projects.length });
    return data.projects.map(toProjectConfig);
  }

  /**
   * Create a new project.
   */
  async createProject(request: CreateProjectRequest): Promise<ProjectConfig> {
    this.log.debug('Creating project', { action: 'createProject', name: request.name });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/projects`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to create project', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to create project');
    }

    const data: ProjectApiResponse = await response.json();
    this.log.info('Project created', { action: 'createProject', id: data.id });
    return toProjectConfig(data);
  }

  /**
   * Update an existing project.
   */
  async updateProject(projectId: string, request: UpdateProjectRequest): Promise<ProjectConfig> {
    this.log.debug('Updating project', { action: 'updateProject', projectId });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/projects/${projectId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to update project', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to update project');
    }

    const data: ProjectApiResponse = await response.json();
    this.log.info('Project updated', { action: 'updateProject', id: data.id });
    return toProjectConfig(data);
  }

  /**
   * Delete a project.
   */
  async deleteProject(projectId: string): Promise<void> {
    this.log.debug('Deleting project', { action: 'deleteProject', projectId });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/projects/${projectId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok && response.status !== 204) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to delete project', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to delete project');
    }

    this.log.info('Project deleted', { action: 'deleteProject', projectId });
  }

  /**
   * Archive a project (soft delete).
   */
  async archiveProject(projectId: string): Promise<ProjectConfig> {
    return this.updateProject(projectId, { isArchived: true });
  }

  /**
   * Get full template metadata for all templates assigned to a project.
   */
  async getProjectTemplates(projectId: string): Promise<TemplateMetadata[]> {
    this.log.debug('Getting project templates', { action: 'getProjectTemplates', projectId });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/projects/${projectId}/templates`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to get project templates', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to get project templates');
    }

    const data = await response.json();
    return data.templates;
  }

  /**
   * Atomically assign a template to a project.
   * Optionally creates the template first if template body is provided.
   */
  async assignTemplate(
    projectId: string,
    options: { template?: SaveTemplateRequest; templateId?: string }
  ): Promise<{ template: Partial<TemplateMetadata>; project: { id: string; name: string; templateIds: string[]; updatedAt: string } }> {
    this.log.debug('Assigning template to project', { action: 'assignTemplate', projectId });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/projects/${projectId}/templates`, {
      method: 'POST',
      headers,
      body: JSON.stringify(options),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to assign template', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to assign template');
    }

    const data = await response.json();
    this.log.info('Template assigned to project', { action: 'assignTemplate', projectId, templateId: data.template?.id });
    return data;
  }
}

// Export singleton instance
export const projectsApi = new ProjectsApiService();

// Export class for testing or custom instances
export { ProjectsApiService };
